import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMedia } from 'react-use';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { OverlappingAvatars } from '@sorare/core/src/components/rivals/OverlappingAvatars';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { uniqueBy } from '@sorare/core/src/lib/arrays';
import { fantasy, glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { breakpoints } from '@sorare/core/src/style/mediaQuery';

import { TrckGroupPVECompetitionPrizePool } from '#src/components/FixtureCompetitionTrckPage/TrckGroupPVECompetitionPrizePool/index.tsx';
import { TaskDetails } from '#src/components/compose/tasks/TaskDetails/index.tsx';
import { ContenderDrawerTabs } from '#src/components/contender/ContenderDrawer/types.ts';
import { FixtureTimeline } from '#src/components/fixture/FixtureTimeline/index.tsx';
import { ComposeRules } from '#src/components/leaderboard/ComposeRules/index.tsx';
import { MyCircleLeaderboard } from '#src/components/leaderboard/MyCircleLeaderboard/index.tsx';
import { PrizePoolWidget } from '#src/components/leaderboard/PrizePoolWidget/index.tsx';
import { LineupBonusDetails } from '#src/components/lineup/LineupBonusDetails/index.tsx';
import { SpecialRoomReward } from '#src/components/rewards/SpecialRoomReward/index.tsx';
import { isFixtureOpened } from '#src/lib/so5.ts';

import type { ContenderDrawerContent_so5LeaderboardContender } from './__generated__/index.graphql.ts';

export const FOOTBALL_TABS = [
  ContenderDrawerTabs.PRIZE_POOL,
  ContenderDrawerTabs.DETAILS,
];

export const NBA_TABS = [
  ContenderDrawerTabs.DETAILS,
  ContenderDrawerTabs.PRIZE_POOL,
];

const getContenderDrawerTabs = ({
  sport,
  hasLineup,
}: {
  sport: Sport;
  hasLineup: boolean;
}): ContenderDrawerTabs[] => {
  return [
    ...(sport === Sport.FOOTBALL ? FOOTBALL_TABS : NBA_TABS),
    hasLineup ? ContenderDrawerTabs.BONUS : undefined,
  ].filter(Boolean);
};

const getTabMessage = ({
  tab,
  isUpcoming,
  hasLineup,
}: {
  sport?: Sport;
  tab: ContenderDrawerTabs;
  isUpcoming: boolean;
  hasLineup: boolean;
}) => {
  if (isUpcoming) {
    return {
      [ContenderDrawerTabs.PRIZE_POOL]: fantasy.prizePool,
      [ContenderDrawerTabs.DETAILS]: hasLineup
        ? navLabels.schedule
        : fantasy.rules,
      [ContenderDrawerTabs.BONUS]: fantasy.bonuses,
    }[tab];
  }
  return {
    [ContenderDrawerTabs.PRIZE_POOL]: fantasy.leaderboard,
    [ContenderDrawerTabs.DETAILS]: navLabels.schedule,
    [ContenderDrawerTabs.BONUS]: fantasy.bonuses,
  }[tab];
};

const Root = styled(Vertical).attrs({ gap: 0 })`
  max-height: 100%;
  min-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;
const Header = styled(SBHorizontal)`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: var(--double-unit);
  background: var(--c-black);
`;

const Footer = styled.footer`
  position: sticky;
  bottom: 0;
  padding: var(--double-unit);
  margin-top: auto;
`;

type Props = {
  tab: ContenderDrawerTabs;
  onChangeTab: (tab: ContenderDrawerTabs) => void;
  onClose?: () => void;
  so5LeaderboardContender: ContenderDrawerContent_so5LeaderboardContender;
  onClickGame?: (gameId: string) => void;
};

export const ContenderDrawerContent = ({
  tab,
  onChangeTab,
  onClose,
  so5LeaderboardContender,
  onClickGame,
}: Props) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [showCircle, setShowCircle] = useState(false);
  const isSeAndBelow = useMedia(`(max-width: ${breakpoints.se}px)`);
  const { so5Leaderboard, so5Lineup } = so5LeaderboardContender;
  const { so5Fixture, myFriendsPlaying } = so5Leaderboard;
  const isUpcoming = isFixtureOpened(so5Fixture);
  const { sport } = useSportContext();

  const displayTabs = getContenderDrawerTabs({
    sport,
    hasLineup: !!so5Lineup,
  });

  const games = uniqueBy(
    game => game.id,
    so5LeaderboardContender.so5Lineup?.so5Appearances.flatMap(appearance =>
      appearance.eligiblePlayerGameScores.map(pgs => pgs.anyGame)
    ) || []
  );

  const tresholdTask = so5Leaderboard.myTasks.find(
    isTypeFn('ThresholdsStreakTask')
  );

  return (
    <Root>
      <Header>
        <TabBar variant="primary" compact={isSeAndBelow} value={tab}>
          {Object.values(displayTabs).map(t => (
            <Tab
              key={t}
              value={t}
              onClick={() => {
                onChangeTab(t);
              }}
            >
              <FormattedMessage
                {...getTabMessage({
                  tab: t,
                  isUpcoming,
                  hasLineup: !!so5Lineup,
                })}
              />
            </Tab>
          ))}
        </TabBar>
        {!!onClose && (
          <IconButton
            size="medium"
            onClick={onClose}
            color="transparent"
            icon={iconX}
            title={formatMessage(glossary.close)}
          />
        )}
      </Header>
      {tab === ContenderDrawerTabs.PRIZE_POOL && (
        <FullWidthContainer>
          {showCircle ? (
            <MyCircleLeaderboard leaderboard={so5Leaderboard.slug} />
          ) : (
            <Vertical gap={2}>
              {!!isUpcoming && !!so5Leaderboard.roomsConfig && (
                <SpecialRoomReward roomsConfig={so5Leaderboard.roomsConfig} />
              )}
              {so5Leaderboard?.pveOnly ? (
                <>
                  <TrckGroupPVECompetitionPrizePool
                    so5Leaderboard={so5Leaderboard}
                    thresholdsStreakTask={so5Lineup?.thresholdsStreakTask}
                  />
                </>
              ) : (
                <>
                  {!!tresholdTask && !!isUpcoming && (
                    <TaskDetails task={tresholdTask} />
                  )}
                  <PrizePoolWidget
                    hideTitle
                    so5Leaderboard={so5Leaderboard}
                    so5Lineup={so5Lineup || undefined}
                    so5LeaderboardContender={so5LeaderboardContender}
                    withCollapsibleRanking
                  />
                </>
              )}
            </Vertical>
          )}
        </FullWidthContainer>
      )}
      {tab === ContenderDrawerTabs.DETAILS && (
        <FullWidthContainer>
          {!so5Lineup && <ComposeRules so5Leaderboard={so5Leaderboard} />}
          {!!games?.length && (
            <FixtureTimeline
              so5Leaderboard={so5Leaderboard}
              so5Lineup={so5Lineup}
              onClickGame={onClickGame}
            />
          )}
        </FullWidthContainer>
      )}
      {!!so5Lineup && tab === ContenderDrawerTabs.BONUS && (
        <LineupBonusDetails
          bonuses={so5Lineup.bonuses}
          bonusRating={so5Lineup.bonusRating}
          bonusPoints={so5Lineup.bonusPoints}
          appearances={so5Lineup.so5Appearances}
        />
      )}
      {tab === ContenderDrawerTabs.PRIZE_POOL && (
        <Footer>
          <SBHorizontal>
            <TabBar variant="primary" value={`${showCircle}`}>
              <Tab value="false" onClick={() => setShowCircle(false)}>
                {so5Fixture.shortDisplayName}
              </Tab>
              {!!myFriendsPlaying.totalCount && (
                <Tab value="true" onClick={() => setShowCircle(true)}>
                  <OverlappingAvatars
                    size={3}
                    key={so5Fixture.slug}
                    additionalPlayersCount={
                      myFriendsPlaying.totalCount -
                      (myFriendsPlaying.so5Rankings.length + 1)
                    }
                    avatars={myFriendsPlaying.so5Rankings.map(({ user }) => ({
                      slug: user.slug,
                      name: user.nickname,
                      avatarUrl: user.pictureUrl,
                    }))}
                  />
                </Tab>
              )}
            </TabBar>
            <div id="contender-drawer-content-sticky-footer" />
          </SBHorizontal>
        </Footer>
      )}
    </Root>
  );
};

ContenderDrawerContent.fragments = {
  so5LeaderboardContender: gql`
    fragment ContenderDrawerContent_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        pveOnly
        myFriendsPlaying: so5RankingsPaginated(
          pageSize: 2
          onlyFollowed: true
        ) {
          totalCount
          so5Rankings {
            id
            user {
              slug
              nickname
              pictureUrl
            }
          }
        }
        so5Fixture {
          slug
          shortDisplayName
          ...isFixtureOpened_so5Fixture
        }
        roomsConfig {
          slug
          ...SpecialRoomReward_roomsConfig
        }
        myTasks {
          id
          ...TasksDetails_thresholdsStreakTask
        }
        ...FixtureTimeline_so5Leaderboard
        ...ComposeRules_so5Leaderboard
        ...PrizePoolWidget_so5Leaderboard
        ...TrckGroupPVECompetitionPrizePool_so5Leaderboard
      }
      so5Lineup {
        id
        thresholdsStreakTask {
          id
          ...TrckGroupPVECompetitionPrizePool_thresholdsStreakTask
        }
        bonusRating
        bonusPoints
        bonuses {
          ...LineupBonusDetails_so5AppearanceBonusInterface
        }
        so5Appearances {
          id
          bonusRating
          eligiblePlayerGameScores {
            id
            anyGame {
              id
            }
          }
          ...LineupBonusDetails_so5Appearance
        }
        ...FixtureTimeline_so5Lineup
      }
      ...PrizePoolWidget_so5LeaderboardContender
    }
    ${ComposeRules.fragments.so5Leaderboard}
    ${PrizePoolWidget.fragments.so5Leaderboard}
    ${PrizePoolWidget.fragments.so5LeaderboardContender}
    ${FixtureTimeline.fragments.so5Leaderboard}
    ${FixtureTimeline.fragments.so5Lineup}
    ${LineupBonusDetails.fragments.so5Appearance}
    ${LineupBonusDetails.fragments.so5AppearanceBonusInterface}
    ${SpecialRoomReward.fragments.roomsConfig}
    ${TaskDetails.fragments.task}
    ${TrckGroupPVECompetitionPrizePool.fragments.so5Leaderboard}
    ${TrckGroupPVECompetitionPrizePool.fragments.thresholdStreakTask}
  ` as TypedDocumentNode<ContenderDrawerContent_so5LeaderboardContender>,
};
